import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  UserCheck,
  UserX,
  ShieldCheck,
  Shield,
  Phone,
  Mail,
  Crown,
  Clock,
  Info,
} from "lucide-react";

const ROLE_META = {
  admin: {
    label: "Admin",
    color: "bg-navy text-white",
    icon: <ShieldCheck className="h-3 w-3" />,
    desc: "Accès total (idem fondateur)",
  },
  operator: {
    label: "Opérateur",
    color: "bg-emerald-100 text-emerald-800",
    icon: <Shield className="h-3 w-3" />,
    desc: "Voit + modifie prenotazioni & contratti · pas de gestion codes promo / équipe",
  },
};

const emptyForm = { email: "", name: "", role: "operator", phone: "", notes: "" };

export default function TeamTab() {
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // "new" | member.email
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/team-members");
      setMembers(r.data.members || []);
    } catch {
      toast.error("Erreur chargement équipe");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (m) => {
    setForm({
      email: m.email,
      name: m.name || "",
      role: m.role || "operator",
      phone: m.phone || "",
      notes: m.notes || "",
    });
    setEditing(m.email);
  };
  const closeForm = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.email.trim() || !form.name.trim()) {
      toast.error("Email et nom requis");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/team-members", {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          role: form.role,
          phone: form.phone.trim(),
          notes: form.notes.trim(),
        });
        toast.success(`${form.name} ajouté à l'équipe`);
      } else {
        await api.patch(`/admin/team-members/${encodeURIComponent(editing)}`, {
          name: form.name.trim(),
          role: form.role,
          phone: form.phone.trim(),
          notes: form.notes.trim(),
        });
        toast.success(`${form.name} mis à jour`);
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const suspend = async (m) => {
    const suspended = !m.suspended_at;
    if (suspended && !window.confirm(`Suspendre ${m.name} ? Ses sessions actives seront révoquées.`)) return;
    try {
      await api.patch(`/admin/team-members/${encodeURIComponent(m.email)}`, { suspended });
      toast.success(suspended ? `${m.name} suspendu` : `${m.name} réactivé`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${m.name} de l'équipe ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/team-members/${encodeURIComponent(m.email)}`);
      toast.success(`${m.name} supprimé`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (loading) return <div className="mt-6 text-slate-500">Chargement…</div>;

  const activeCount = members.filter((m) => m.active && !m.suspended_at).length;
  const suspendedCount = members.filter((m) => m.suspended_at).length;

  return (
    <div className="mt-6" data-testid="team-tab">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-navy flex items-center gap-2">
            <Users className="h-5 w-5 text-[#5BA4D4]" />
            Équipe — Collaborateurs
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Whitelist des emails autorisés à se connecter au dashboard admin. Ils feront login avec leur propre compte Google.
          </p>
        </div>
        <button onClick={openNew} className="btn-primary text-sm" data-testid="team-new-btn">
          <Plus className="h-4 w-4" /> Inviter un collaborateur
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <StatCard icon={<UserCheck className="h-4 w-4" />} label="Membres actifs" value={activeCount} testid="team-active" />
        <StatCard icon={<UserX className="h-4 w-4" />} label="Suspendus" value={suspendedCount} testid="team-suspended" color="text-amber-600" />
        <StatCard icon={<Users className="h-4 w-4" />} label="Total" value={members.length} testid="team-total" color="text-slate-600" />
      </div>

      <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex items-start gap-2 mb-5">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          <b>Comment ça marche :</b> tu ajoutes ici l&apos;email d&apos;un collaborateur (nouvelle ou existante sur Google). À sa prochaine connexion sur <code>/admin</code> via le bouton « Se connecter avec Google », il obtiendra automatiquement le rôle que tu as défini. Aucun mot de passe à partager.
        </span>
      </div>

      <div className="grid gap-3">
        {members.map((m) => (
          <TeamRow
            key={m.email}
            m={m}
            onEdit={() => openEdit(m)}
            onSuspend={() => suspend(m)}
            onDelete={() => remove(m)}
          />
        ))}
      </div>

      {editing ? (
        <TeamFormModal
          form={form}
          setForm={setForm}
          isNew={editing === "new"}
          onClose={closeForm}
          onSave={save}
          saving={saving}
        />
      ) : null}
    </div>
  );
}

function StatCard({ icon, label, value, testid, color = "text-[#5BA4D4]" }) {
  return (
    <div className="card-clean p-4" data-testid={testid}>
      <div className={`inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-50 ${color}`}>
        {icon}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
      <p className="text-xl font-bold text-navy mt-0.5">{value}</p>
    </div>
  );
}

function TeamRow({ m, onEdit, onSuspend, onDelete }) {
  const role = ROLE_META[m.role] || ROLE_META.operator;
  const suspended = !!m.suspended_at;
  return (
    <div
      className={`card-clean p-5 ${suspended ? "opacity-60 border-amber-200" : ""}`}
      data-testid={`team-row-${m.email}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-navy">{m.name}</p>
            {m.is_founder ? (
              <span className="rounded-full bg-yellow-100 text-yellow-800 text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide flex items-center gap-1">
                <Crown className="h-3 w-3" /> Fondateur
              </span>
            ) : null}
            <span className={`rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide flex items-center gap-1 ${role.color}`}>
              {role.icon} {role.label}
            </span>
            {suspended ? (
              <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide">
                Suspendu
              </span>
            ) : null}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
            <a href={`mailto:${m.email}`} className="flex items-center gap-1 hover:text-navy">
              <Mail className="h-3 w-3" /> {m.email}
            </a>
            {m.phone ? (
              <a href={`tel:${m.phone}`} className="flex items-center gap-1 hover:text-navy">
                <Phone className="h-3 w-3" /> {m.phone}
              </a>
            ) : null}
            {m.last_login_at ? (
              <span className="flex items-center gap-1 text-slate-500">
                <Clock className="h-3 w-3" />
                Dernier login : {m.last_login_at.slice(0, 10)}
              </span>
            ) : m.is_founder ? null : (
              <span className="flex items-center gap-1 text-slate-400 italic">
                <Clock className="h-3 w-3" /> Jamais connecté
              </span>
            )}
          </div>
          {m.notes ? (
            <p className="mt-2 text-xs text-slate-500 italic">&ldquo;{m.notes}&rdquo;</p>
          ) : null}
          <p className="mt-2 text-[11px] text-slate-400">{role.desc}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-end">
          {m.is_founder ? (
            <p className="text-[11px] text-slate-400 italic max-w-[180px] text-right">
              Le compte fondateur ne peut pas être modifié depuis l&apos;interface.
            </p>
          ) : (
            <>
              <button
                onClick={onSuspend}
                className={`text-xs rounded-lg px-3 py-1.5 font-medium flex items-center gap-1 ${
                  suspended
                    ? "bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
                    : "bg-amber-100 hover:bg-amber-200 text-amber-800"
                }`}
                data-testid={`team-suspend-${m.email}`}
              >
                {suspended ? <><Check className="h-3 w-3" /> Réactiver</> : <><UserX className="h-3 w-3" /> Suspendre</>}
              </button>
              <button
                onClick={onEdit}
                className="text-xs rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-700 px-3 py-1.5 font-medium flex items-center gap-1"
                data-testid={`team-edit-${m.email}`}
              >
                <Pencil className="h-3 w-3" /> Éditer
              </button>
              <button
                onClick={onDelete}
                className="text-xs rounded-lg bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 font-medium flex items-center gap-1"
                data-testid={`team-delete-${m.email}`}
              >
                <Trash2 className="h-3 w-3" /> Supprimer
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TeamFormModal({ form, setForm, isNew, onClose, onSave, saving }) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="team-form-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h4 className="font-semibold text-navy flex items-center gap-2">
            <Users className="h-4 w-4" />
            {isNew ? "Inviter un collaborateur" : `Éditer ${form.email}`}
          </h4>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="team-form-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <Field label="Email Google du collaborateur" required>
            <input
              type="email"
              value={form.email}
              disabled={!isNew}
              onChange={(e) => set("email")(e.target.value.toLowerCase())}
              placeholder="ex: marco.rossi@gmail.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
              data-testid="team-form-email"
              autoFocus
            />
            {!isNew && (
              <p className="mt-1 text-[11px] text-slate-500 italic">L&apos;email ne peut pas être modifié après création.</p>
            )}
          </Field>

          <Field label="Nom complet" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="ex: Marco Rossi"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="team-form-name"
              maxLength={120}
            />
          </Field>

          <Field label="Rôle" required>
            <div className="grid grid-cols-2 gap-2">
              {["admin", "operator"].map((r) => {
                const meta = ROLE_META[r];
                const active = form.role === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => set("role")(r)}
                    className={`text-left rounded-lg border-2 p-3 transition ${
                      active
                        ? "border-navy bg-navy/5"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`team-form-role-${r}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full text-[10px] px-2 py-0.5 font-bold uppercase tracking-wide flex items-center gap-1 ${meta.color}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">{meta.desc}</p>
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="Téléphone (optionnel)">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone")(e.target.value)}
              placeholder="+32 470 …"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="team-form-phone"
              maxLength={40}
            />
          </Field>

          <Field label="Notes internes (optionnel)">
            <textarea
              value={form.notes}
              onChange={(e) => set("notes")(e.target.value)}
              placeholder="ex: Zone Bruxelles Nord, disponible weekends"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm h-20 resize-none"
              data-testid="team-form-notes"
              maxLength={500}
            />
          </Field>

          <div className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Le collaborateur se connectera sur <code>/admin</code> via <b>Google Sign-In</b> avec l&apos;email indiqué. Aucun mot de passe à envoyer.
            </span>
          </div>
        </div>
        <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline text-sm" data-testid="team-form-cancel">Annuler</button>
          <button onClick={onSave} disabled={saving} className="btn-primary text-sm disabled:opacity-40" data-testid="team-form-save">
            {saving ? "…" : isNew ? "Inviter" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-navy">
        {label}
        {required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
